import { sumBy } from 'lodash-es';
import { DateTime } from 'luxon';
import * as React from 'react';
import { Box } from '../../designSystem/components/box';
import { Flex } from '../../designSystem/components/flex';
import { Cell } from '../../designSystem/components/table/cell';
import { HeaderCell } from '../../designSystem/components/table/headerCell';
import { HeaderRow } from '../../designSystem/components/table/headerRow';
import { Row } from '../../designSystem/components/table/row';
import { Table } from '../../designSystem/components/table/table';
import { PsuiUserHoursFragment } from '../../generated/types';
import { DateTimeService } from '../../services/dateTimeService';
import { DurationService } from '../../services/durationService';
import { useService, useTranslate } from '../../util/dependencies/dependencies';
import { LocaleFormats } from '../../util/luxon';

interface IUserHoursDayTableProps {
    day: DateTime;
    psuis: PsuiUserHoursFragment[];
    timeZone: string;
}

export const UserHoursDayTable = (props: IUserHoursDayTableProps) => {
    const translate = useTranslate();
    const dateTimeService = useService(DateTimeService);
    const durationService = useService(DurationService);
    const psuis = React.useMemo(
        () =>
            props.psuis.filter(
                (psui) =>
                    psui.positionSlot.range.start!.startOf('day').toMillis() ===
                    props.day.toMillis()
            ),
        [props.day, props.psuis]
    );
    const total = React.useMemo(() => sumBy(psuis, (psui) => psui.timeDifferenceMinutes), [psuis]);

    return (
        <Flex css={{ bd: true, overflow: 'hidden' }} direction="column" width={1}>
            <Box
                css={{ padding: '$3 $4', textTransform: 'capitalize' }}
                font="gray800 textSm semiBold"
            >
                {dateTimeService.toLocaleString(
                    props.day,
                    LocaleFormats.DateOnly.WeekdayLongMonthLong
                )}
            </Box>

            <Table css={{ borderTop: '1px solid $gray200' }} hideBorder={true}>
                <HeaderRow css={{ height: '32px' }}>
                    <HeaderCell>{translate('nom_de_la_missi_64605')}</HeaderCell>
                    <HeaderCell align="start" direction="column" width={120}>
                        <Box>{translate('d_but_41840')}</Box>

                        <Box css={{ fontSize: '8px' }}>{translate('_point_missi_66864')}</Box>
                    </HeaderCell>
                    <HeaderCell align="start" direction="column" width={120}>
                        <Box>{translate('fin_60104')}</Box>

                        <Box css={{ fontSize: '8px' }}>{translate('_point_missi_66864')}</Box>
                    </HeaderCell>
                    <HeaderCell width={120}>{translate('pause_46709')}</HeaderCell>
                    <HeaderCell width={120}>{translate('total_07173')}</HeaderCell>
                </HeaderRow>

                {psuis.map((psui, index) => (
                    <Row key={index} css={{ height: '32px' }}>
                        <Cell>
                            <Box font="gray800 textXs medium">{psui.position.name}</Box>
                        </Cell>

                        <Cell
                            align="start"
                            css={{ color: '$gray500' }}
                            direction="column"
                            width={120}
                        >
                            <Box>
                                {psui.checkInAt?.isValid
                                    ? dateTimeService.toLocaleString(
                                          psui.checkInAt.setZone(props.timeZone),
                                          LocaleFormats.TimeOnly
                                      )
                                    : '-'}
                            </Box>

                            <Box css={{ fontSize: '8px' }}>
                                {dateTimeService.toLocaleString(
                                    psui.positionSlot.range.start!,
                                    LocaleFormats.TimeOnly
                                )}
                            </Box>
                        </Cell>

                        <Cell
                            align="start"
                            css={{ color: '$gray500' }}
                            direction="column"
                            width={120}
                        >
                            <Box>
                                {psui.checkOutAt?.isValid
                                    ? dateTimeService.toLocaleString(
                                          psui.checkOutAt.setZone(props.timeZone),
                                          LocaleFormats.TimeOnly
                                      )
                                    : '-'}
                            </Box>

                            <Box css={{ fontSize: '8px' }}>
                                {dateTimeService.toLocaleString(
                                    psui.positionSlot.range.end!,
                                    LocaleFormats.TimeOnly
                                )}
                            </Box>
                        </Cell>

                        <Cell css={{ color: '$gray500' }} width={120}>
                            {psui.breakTime > 0
                                ? durationService.minutesToHoursMinutesString(psui.breakTime)
                                : '-'}
                        </Cell>

                        <Cell css={{ color: '$gray500' }} justify="end" width={120}>
                            {durationService.minutesToHoursMinutesString(
                                psui.timeDifferenceMinutes
                            )}
                        </Cell>
                    </Row>
                ))}

                <HeaderRow css={{ borderBottom: 'none', height: '32px' }}>
                    <HeaderCell>
                        <Box font="gray800 textXs semiBold">
                            {translate('total_de_la_jou_73634')}
                        </Box>
                    </HeaderCell>
                    <HeaderCell justify="end" width={120}>
                        <Box font="gray800 textXs semiBold">
                            {durationService.minutesToHoursMinutesString(total)}
                        </Box>
                    </HeaderCell>
                </HeaderRow>
            </Table>
        </Flex>
    );
};
